import { useState, useRef, ChangeEvent, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCategories } from '../lib/categories'
import { useCards } from '../lib/cards'

interface ImportTransactionsModalProps {
  isOpen: boolean
  onClose: () => void
}

interface ImportPreviewRow {
  date: string
  description: string
  amount: string
  type: 'income' | 'expense'
  category: string
  card: string
  is_essential: boolean
  rawData: Record<string, string>
}

interface ImportPreview {
  headers: string[]
  rows: ImportPreviewRow[]
  errors: string[]
}

interface Template {
  id: string
  name: string
  file_format: 'csv' | 'xlsx'
  header_mapping: Record<string, string>
  delimiter: string
  date_format: string
  is_global: boolean
}

interface ImportResponse {
  imported: number
  errors: string[]
}

interface TemplateListResponse {
  templates: Template[]
}

const DEFAULT_COLUMN_NAMES: Record<string, string[]> = {
  date: ['date', 'data', 'transaction_date', 'data da transacao', 'data da transação'],
  description: ['description', 'descricao', 'desc', 'transacao', 'transação'],
  amount: ['amount', 'valor', 'preco', 'preco', 'total'],
  type: ['type', 'tipo'],
  category: ['category', 'categoria', 'categoria_id'],
  card: ['card', 'cartao', 'cartao_id', 'card_id'],
  is_essential: ['is_essential', 'essencial', 'essencial?']
}

export default function ImportTransactionsModal({ isOpen, onClose }: ImportTransactionsModalProps) {
  const [step, setStep] = useState<'upload' | 'preview' | 'processing'>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const { data: categoriesData } = useCategories()
  const { data: cardsData } = useCards()
  
  const categories = categoriesData?.categories ?? []
  const cards = cardsData?.cards ?? []

  // Fetch available templates
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery<TemplateListResponse>({
    queryKey: ['import-templates'],
    queryFn: async () => {
      const res = await api.get<TemplateListResponse>('/v0.1/templates/')
      return res.data
    },
    enabled: isOpen
  })

  const templates = templatesData?.templates ?? []

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async ({ formData, templateId }: { formData: FormData; templateId: string }) => {
      const res = await api.post<ImportResponse>(`/v0.1/transactions/import?template_id=${templateId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
      onClose()
      window.location.reload()
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Erro ao importar transações')
      setStep('upload')
    },
  })

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (!selectedFile.name.endsWith('.csv') && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Formato de arquivo não suportado. Use CSV ou XLSX.')
      return
    }

    setFile(selectedFile)
    setError(null)
    setStep('upload')
  }

  const handleUpload = async () => {
    if (!file) return

    setStep('preview')
    
    try {
      if (file.name.endsWith('.csv')) {
        await parseCsvFile()
      } else {
        await parseXlsxFile()
      }
    } catch (err) {
      setError('Erro ao ler o arquivo. Verifique o formato.')
      setStep('upload')
    }
  }

  const parseCsvFile = async () => {
    if (!file) return

    const text = await file.text()
    const lines = text.trim().split('\n')
    
    if (lines.length < 2) {
      setError('O arquivo parece estar vazio ou não possui dados.')
      setStep('upload')
      return
    }

    const delimiter = detectDelimiter(lines[0])
    const headers = parseLine(lines[0], delimiter)
    
    const previewRows: ImportPreviewRow[] = []
    for (let i = 1; i < Math.min(lines.length, 10); i++) {
      const row = parseLine(lines[i], delimiter)
      if (row.length > 0 && row.some((v: string) => v.trim())) {
        previewRows.push(createPreviewRow(row, headers))
      }
    }

    if (previewRows.length === 0) {
      setError('Nenhuma linha de dados válida encontrada.')
      setStep('upload')
      return
    }

    setPreview({ headers, rows: previewRows, errors: [] })
  }

  const parseXlsxFile = async () => {
    if (!file) return

    // For XLSX files, we'll use a simple approach
    // The backend will handle actual XLSX parsing
    const arrayBuffer = await file.arrayBuffer()
    
    // Create a simple preview from the file
    // In production, you'd use a library like SheetJS to parse XLSX client-side
    const headerLine = 'date;description;amount;type;category_id;card_id'
    const headers = headerLine.split(';')
    
    setPreview({ 
      headers, 
      rows: [{
        date: '',
        description: '',
        amount: '',
        type: 'expense',
        category: categories[0]?.name || 'Outros',
        card: cards[0]?.name || 'Nenhum',
        is_essential: false,
        rawData: {}
      }], 
      errors: ['XLSX preview não disponível. O mapeamento será feito pelo backend usando o template selecionado.']
    })
  }

  const handleImport = () => {
    if (!file || !selectedTemplateId) return
    
    setStep('processing')
    const formData = new FormData()
    formData.append('file', file)
    importMutation.mutate({ formData, templateId: selectedTemplateId })
  }

  const resetModal = () => {
    setStep('upload')
    setFile(null)
    setPreview(null)
    setSelectedTemplateId(null)
    setError(null)
  }

  const closeModal = () => {
    if (importMutation.isPending) return
    resetModal()
    onClose()
  }

  // Auto-select first template if only one available
  useEffect(() => {
    if (templates.length === 1 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id)
    }
  }, [templates])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[var(--color-surface)] rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">
            {step === 'upload' && 'Importar Transações'}
            {step === 'preview' && 'Pré-visualização'}
            {step === 'processing' && 'Importando...'}
          </h3>
          <button
            onClick={closeModal}
            className="rounded-full p-1 hover:bg-[var(--color-surface-subtle)] transition-colors"
            disabled={step === 'processing'}
          >
            <svg xmlns="http://www.w3.org/svg" className="h-5 w-5 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M18 12a.75.75 0 01-.75.75H12v4.75a.75.75 0 01-1.5 0V12.75H4.75a.75.75 0 010-1.5h4.75V6.25a.75.75 0 011.5 0v4.75h4.75a.75.75 0 01.75.75z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-[var(--color-danger-light)] text-[var(--color-danger)]">
              {error}
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-[var(--color-border)] rounded-lg p-8 text-center hover:border-[var(--color-brand-400)] transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg xmlns="http://www.w3.org/svg" className="h-12 w-12 mx-auto text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9.5a1 1 0 011-1h4.59L12 5.41a1 1 0 011.41 0l3 3.59H19a1 1 0 110 2h-2.59l-3 3.59a1 1 0 01-1.41 0l-3-3.59H6a1 1 0 01-1-1V9.5z" clipRule="evenodd" />
                </svg>
                <p className="mt-2 text-sm text-[var(--color-text)]">
                  <span className="font-medium text-[var(--color-brand-500)]">Clique para selecionar</span> um arquivo CSV ou XLSX
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Arraste o arquivo para cima também é suportado
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              
              <div className="text-xs text-[var(--color-text-muted)]">
                <strong>Formato esperado:</strong>
                <ul className="mt-1 list-disc list-inside">
                  <li>CSV: colunas separadas por ponto e vírgula (;) ou vírgula (,)</li>
                  <li>XLSX: primeira linha como cabeçalho</li>
                </ul>
                <br />
                <strong>Colunas suportadas:</strong>
                <ul className="mt-1 list-disc list-inside">
                  <li>Data/transaction_date: data da transação</li>
                  <li>Descrição/Descricao: descrição da transação</li>
                  <li>Valor/Amount: valor numérico</li>
                  <li>Tipo: income/entrada ou expense/saída</li>
                  <li>Categoria_ID: UUID da categoria</li>
                  <li>Card_ID: UUID do cartão (opcional)</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-[var(--color-text)] mb-2">Colunas detectadas</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {preview.headers.length > 0 ? (
                    preview.headers.map((header, idx) => {
                      const detectedField = Object.keys(DEFAULT_COLUMN_NAMES).find(
                        field => DEFAULT_COLUMN_NAMES[field]?.includes(header.toLowerCase())
                      )
                      return (
                        <span 
                          key={idx} 
                          className={"px-2 py-1 rounded text-xs " + (detectedField ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]' : 'bg-[var(--color-surface-subtle)] text-[var(--color-text-muted)]')}
                        >
                          {header} {detectedField && ' → ' + detectedField}
                        </span>
                      )
                    })
                  ) : (
                    <span className="text-xs text-[var(--color-text-muted)]">Nenhuma coluna detectada</span>
                  )}
                </div>
              </div>

              {preview.errors.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-[var(--color-warning-light)] text-[var(--color-warning)] text-sm">
                  {preview.errors[0]}
                </div>
              )}

              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
                    Template de importação
                  </label>
                  <select
                    value={selectedTemplateId || ''}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
                  >
                    <option value="">Selecione um template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.is_global && <span className="text-[var(--color-text-muted)]">(global)</span>}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Use um template para mapear colunas do arquivo para campos da transação.
                    {templates.length === 0 && ' Nenhum template disponível. Crie um template em Configurações.'}
                  </p>
                </div>
              )}

              {preview.rows.length > 0 && (
                <>
                  <h4 className="text-sm font-medium text-[var(--color-text)] mb-2">
                    Pré-visualização ({Math.min(preview.rows.length, 5)} linhas)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--color-surface-subtle)]">
                          <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">Data</th>
                          <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">Descrição</th>
                          <th className="px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">Valor</th>
                          <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 5).map((row, idx) => (
                          <tr key={idx} className="border-b border-[var(--color-border)]">
                            <td className="px-3 py-2">{row.date || '—'}</td>
                            <td className="px-3 py-2">{row.description || '—'}</td>
                            <td className="px-3 py-2 text-right">{row.amount || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={"px-2 py-0.5 rounded text-xs font-medium " + (row.type === 'income' ? 'bg-[var(--color-success-light)] text-[var(--color-success)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger)]')}>
                                {row.type === 'income' ? 'Entrada' : 'Saída'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
              <p className="text-sm text-[var(--color-text)]">Importando transações...</p>
              <p className="text-xs text-[var(--color-text-muted)]">Esta operação pode levar alguns momentos.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
          {step !== 'upload' && step !== 'processing' && (
            <button
              type="button"
              onClick={() => setStep('upload')}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-subtle)]"
            >
              Voltar
            </button>
          )}
          
          {step === 'upload' && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file}
              className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-600)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuar
            </button>
          )}

          {step === 'preview' && (
            <button
              type="button"
              onClick={handleImport}
              disabled={!selectedTemplateId || importMutation.isPending}
              className="rounded-lg bg-[var(--color-success)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-success-dark)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importMutation.isPending ? 'Importando...' : 'Confirmar Importação'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper functions
function detectDelimiter(headerLine: string): string {
  const delimiters = [';', ',', '\t', '|']
  const counts: Record<string, number> = {}
  
  delimiters.forEach(d => {
    counts[d] = (headerLine.match(new RegExp('\\' + d, 'g')) || []).length
  })
  
  return Object.entries(counts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
}

function parseLine(line: string, delimiter: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    
    if (char === '"') {
      inQuotes = !inQuotes
      current += char
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  
  result.push(current.trim())
  return result.map(c => c.replace(/^"|"$/g, ''))
}

function createPreviewRow(row: string[], headers: string[]): ImportPreviewRow {
  const rowData: Record<string, string> = {}
  headers.forEach((header, idx) => {
    rowData[header] = row[idx] || ''
  })
  
  // Extract values
  const rawDate = rowData['date'] || rowData['data'] || rowData['transaction_date'] || ''
  const rawDescription = rowData['description'] || rowData['descricao'] || rowData['desc'] || ''
  const rawAmount = rowData['amount'] || rowData['valor'] || ''
  const rawType = rowData['type'] || rowData['tipo'] || 'expense'
  
  // Parse amount
  let amount = '0,00'
  if (rawAmount) {
    const cleaned = rawAmount.replace(/[^\d.,]/g, '').replace(',', '.')
    const num = parseFloat(cleaned)
    if (!isNaN(num)) {
      amount = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      }).format(num)
    }
  }
  
  // Parse date
  const parseDate = (dateStr: string): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0]
    
    const cleaned = dateStr.trim()
    
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return cleaned
    }
    
    const parts = cleaned.split(/[-/\\.]/)
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      
      if (year >= 1000 && year <= 9999) {
        if (month <= 12 && day <= 31) {
          return year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0')
        } else {
          return year + '-' + String(day).padStart(2, '0') + '-' + String(month).padStart(2, '0')
        }
      }
    }
    
    return cleaned
  }
  
  const type = rawType.toLowerCase() === 'income' ? 'income' : 'expense'
  
  return {
    date: parseDate(rawDate),
    description: rawDescription,
    amount,
    type,
    category: '—',
    card: '',
    is_essential: false,
    rawData: rowData
  }
}
